import { Fragment, useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import CountryPicker from '../../components/CountryPicker';
import { tokens } from '../../components/forms/tokens';
import { getApiErrorMessage } from '../../lib/apiError';
import adminService from '../../services/admin.service';
import type {
  AnnouncedEntity,
  ConcentrationAccount,
  ConcentrationAccountCreateBody,
  ConcentrationAccountPatchBody,
  ReceivingEntity,
} from '../../types/finance';
import { approveMfaErrorMessage } from '../../lib/mfaApproval';
import { useMovablePopup } from '../../components/MovablePopup';
import {
  STANDING_LABEL,
  accountStanding,
  announcedChoices,
  detailsFrozen,
  entityOption,
  fundLabel,
  fundProblem,
  fundsWithoutAccount,
  holderPlaceRows,
  mayConfirm,
  waitingNote,
} from './concentrationAccounts.helpers';
import { FIRST_COUNTRIES, readerCountryName } from '../../lib/countries';

type ModalState =
  | { kind: 'create'; entity: ReceivingEntity | null }
  | { kind: 'edit'; account: ConcentrationAccount };

function bankDetails(a: ConcentrationAccount): string {
  if (a.market === 'MX') return a.clabe ?? '—';
  if (a.market === 'CA')
    return [a.transit_number, a.institution_number, a.account_number]
      .filter(Boolean)
      .join(' / ') || '—';
  return [a.routing_number, a.account_number].filter(Boolean).join(' / ') || '—';
}

export default function AdminConcentrationAccounts(): JSX.Element {
  const [accounts, setAccounts] = useState<ConcentrationAccount[]>([]);
  // Which funds receive client money, and which of them have no account. A failure to
  // read it is said on its own: it must not look like "nothing is missing".
  const [entities, setEntities] = useState<ReceivingEntity[]>([]);
  const [entitiesErr, setEntitiesErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      setAccounts(await adminService.getConcentrationAccounts());
    } catch (e) {
      setErr(getApiErrorMessage(e, 'Failed to load concentration accounts'));
    } finally {
      setLoading(false);
    }
    setEntitiesErr(null);
    try {
      setEntities(await adminService.getReceivingEntities());
    } catch (e) {
      setEntities([]);
      setEntitiesErr(
        getApiErrorMessage(e, 'Could not read which funds are receiving client money.'),
      );
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSaved = (msg: string): void => {
    setFlash(msg);
    setModal(null);
    load();
    globalThis.setTimeout(() => setFlash(null), 5000);
  };

  return (
    <div style={{ padding: 24, color: tokens.textPrimary }}>
      <PageHeader
        title="Concentration accounts"
        subtitle="Each fund has its own bank account. Clients of a product are shown the account of the fund that receives it."
      />

      {err && <div style={errBoxStyle}>{err}</div>}
      {flash && <div style={successBoxStyle}>{flash}</div>}
      {entitiesErr && <div style={errBoxStyle}>{entitiesErr}</div>}

      <MissingAccounts
        entities={fundsWithoutAccount(entities)}
        onAdd={(entity) => setModal({ kind: 'create', entity })}
      />

      <HolderPlaces entities={entities} />

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button
          type="button"
          style={btnPrimary}
          onClick={() => setModal({ kind: 'create', entity: null })}
        >
          Add account
        </button>
      </div>

      <div style={cardStyle}>
        {(() => {
          if (loading) {
            return <p style={{ color: tokens.textMuted }}>Loading…</p>;
          }
          if (accounts.length === 0) {
            return <p style={{ color: tokens.textMuted }}>No concentration accounts configured.</p>;
          }
          return (
            <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr style={{ color: tokens.textMuted, borderBottom: `1px solid ${tokens.border}` }}>
                  <th style={thStyle}>Bank country</th>
                  <th style={thStyle}>Currency</th>
                  <th style={thStyle}>Beneficiary</th>
                  <th style={thStyle}>Fund</th>
                  <th style={thStyle}>Method</th>
                  <th style={thStyle}>Bank details</th>
                  <th style={thStyle}>Standing</th>
                  <th style={thStyle}>Effective</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((a) => (
                  <tr key={a.id} style={{ borderBottom: `1px solid ${tokens.border}` }}>
                    <td style={tdStyle}>{a.market}</td>
                    <td style={tdStyle}>{a.currency}</td>
                    <td style={tdStyle}>{a.beneficiary}</td>
                    <td style={{ ...tdStyle, fontSize: 12 }}>{fundLabel(a)}</td>
                    <td style={tdStyle}>{a.transfer_method}</td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 12 }}>
                      {bankDetails(a)}
                    </td>
                    <td style={tdStyle}>
                      <StandingBadge account={a} />
                      {accountStanding(a) === 'waiting' && (
                        <div style={{ fontSize: 11, marginTop: 4, color: tokens.textMuted, maxWidth: 220 }}>
                          {waitingNote(a)}
                        </div>
                      )}
                    </td>
                    <td style={{ ...tdStyle, fontSize: 12 }}>
                      {a.effective_from} → {a.effective_to ?? 'now'}
                    </td>
                    <td style={tdStyle}>
                      {/* One row of actions, right-aligned: a waiting account's Confirm beside its Edit. */}
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'flex-start' }}>
                        {mayConfirm(a) && (
                          <ConfirmAccount
                            account={a}
                            onConfirmed={() => handleSaved('Account confirmed. It is in force now.')}
                          />
                        )}
                        <button
                          type="button"
                          style={{ ...btnSecondary, whiteSpace: 'nowrap' }}
                          onClick={() => setModal({ kind: 'edit', account: a })}
                        >
                          {detailsFrozen(a) ? 'Retire' : 'Edit'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          );
        })()}
      </div>

      {modal && (
        <AccountModal
          state={modal}
          missing={fundsWithoutAccount(entities)}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

function StandingBadge({ account }: Readonly<{ account: ConcentrationAccount }>): JSX.Element {
  const standing = accountStanding(account);
  const good = standing === 'in_force';
  return (
    <span
      style={{
        padding: '2px 8px',
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 600,
        background: good ? tokens.successBg : tokens.errorBg,
        color: good ? tokens.successText : tokens.errorText,
        border: `1px solid ${good ? tokens.successBorder : tokens.errorBorder}`,
      }}
    >
      {STANDING_LABEL[standing]}
    </span>
  );
}

/** The second person's act: the code, then the account is in force. */
function ConfirmAccount({ account, onConfirmed }: Readonly<{
  account: ConcentrationAccount;
  onConfirmed: () => void;
}>): JSX.Element {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await adminService.confirmConcentrationAccount(account.id, code.trim());
      onConfirmed();
    } catch (e) {
      setError(approveMfaErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button type="button" style={{ ...btnPrimary, whiteSpace: 'nowrap' }} onClick={() => setOpen(true)}>
        Confirm
      </button>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 6, textAlign: 'left', maxWidth: 260 }}>
      <label style={{ ...labelStyle, fontSize: 12 }} htmlFor={`confirm-${account.id}`}>
        Your authentication code
      </label>
      <input
        id={`confirm-${account.id}`}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        value={code}
        disabled={busy}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
        placeholder="6-digit code"
        style={{ ...inputStyle, fontFamily: 'monospace' }}
      />
      {error && <div style={errBoxStyle} role="alert">{error}</div>}
      <p style={{ margin: 0, fontSize: 11, color: tokens.textMuted }}>
        Confirming shows this account to the fund’s clients and retires the fund’s previous one.
      </p>
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="button" style={btnPrimary} disabled={busy || code.length < 6}
                onClick={() => { void confirm(); }}>
          {busy ? 'Confirming…' : 'Put it in force'}
        </button>
        <button type="button" style={btnSecondary} disabled={busy} onClick={() => setOpen(false)}>
          Not now
        </button>
      </div>
    </div>
  );
}

/** The funds that receive client money and have no account: what has to be fixed
 *  before the clients of those products can fund. Nothing is rendered when none is. */
/** Where each Mexican issuer stands against its holder limit: who is counted, who is in the grace of a
 *  former client, who only signed, who left, and the room that is left. No names. */
export function HolderPlaces({ entities }: Readonly<{ entities: ReceivingEntity[] }>): JSX.Element | null {
  const shown = entities.filter((e) => holderPlaceRows(e).length > 0);
  if (shown.length === 0) return null;
  return (
    <div style={{ ...cardStyle, marginBottom: 16 }}>
      <p style={{ margin: '0 0 8px', fontWeight: 600 }}>Places of each Mexican fund</p>
      {shown.map((e) => (
        <div key={e.legal_entity_id} style={{ marginBottom: 12, fontSize: 13 }}>
          <div style={{ fontWeight: 600 }}>{e.products.join(', ')}</div>
          <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 12 }}>
            {holderPlaceRows(e).map(([label, value]) => (
              <Fragment key={label}>
                <dt style={{ color: tokens.textMuted }}>{label}</dt>
                <dd style={{ margin: 0 }}>{value}</dd>
              </Fragment>
            ))}
          </dl>
        </div>
      ))}
    </div>
  );
}

export function MissingAccounts({ entities, onAdd }: Readonly<{
  entities: ReceivingEntity[];
  onAdd: (entity: ReceivingEntity) => void;
}>): JSX.Element | null {
  if (entities.length === 0) return null;
  return (
    <div style={{ ...errBoxStyle, marginBottom: 16 }} role="alert">
      <p style={{ margin: '0 0 8px', fontWeight: 600 }}>
        {entities.length === 1 ? 'One fund receives' : `${entities.length} funds receive`} client
        money and {entities.length === 1 ? 'has' : 'have'} no bank account registered.
      </p>
      <p style={{ margin: '0 0 8px', fontSize: 13 }}>
        Clients of these products are shown no account to transfer to, and a transfer sent to
        any other account is not credited.
      </p>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
        {entities.map((e) => (
          <li key={e.legal_entity_id} style={{ marginBottom: 4 }}>
            {entityOption(e)}{' '}
            <button type="button" style={btnSecondary} onClick={() => onAdd(e)}>
              Add its account
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The fund of an account entered before the fund receives capital (a new series): picked from
 * the companies books announced that have no account yet. Only when no list can be built (books
 * announced none, or it could not be read) is the company's id typed, and the screen says why.
 */
export function NotReceivingFund({
  offeredAbove,
  value,
  onPick,
}: Readonly<{
  offeredAbove: ReceivingEntity[];
  value: string;
  onPick: (legalEntityId: string, currency: string | null) => void;
}>): JSX.Element {
  const [announced, setAnnounced] = useState<AnnouncedEntity[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    adminService
      .getAnnouncedEntities()
      .then((found) => {
        if (live) setAnnounced(found);
      })
      .catch((e: unknown) => {
        if (!live) return;
        setAnnounced([]);
        setLoadErr(getApiErrorMessage(e, 'Could not read the companies books announced.'));
      });
    return () => {
      live = false;
    };
  }, []);

  if (announced === null) {
    return <p style={noteStyle}>Reading the companies books announced…</p>;
  }
  const choices = announcedChoices(announced, offeredAbove, readerCountryName);
  if (choices.length > 0) {
    return (
      <>
        <label style={labelStyle}>
          Its company, as books announced it{' '}
          <select
            value={value}
            onChange={(e) => {
              const picked = choices.find((c) => c.legal_entity_id === e.target.value);
              onPick(e.target.value, picked?.currency ?? null);
            }}
            style={inputStyle}
          >
            <option value="">Choose a company…</option>
            {choices.map((c) => (
              <option key={c.legal_entity_id} value={c.legal_entity_id}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
        <p style={noteStyle}>
          Not listed? Books has not announced that company yet; it appears here once it has.
        </p>
      </>
    );
  }
  return (
    <>
      <p style={noteStyle}>
        {loadErr ?? 'Books has not announced this company yet; it appears here once it has.'}
      </p>
      <label style={labelStyle}>
        Its legal entity id, from the funds console{' '}
        <input
          value={value}
          onChange={(e) => onPick(e.target.value, null)}
          style={{ ...inputStyle, fontFamily: 'monospace' }}
          placeholder="00000000-0000-0000-0000-000000000000"
        />
      </label>
    </>
  );
}

function AccountModal({
  state,
  missing,
  onClose,
  onSaved,
}: Readonly<{
  state: ModalState;
  missing: ReceivingEntity[];
  onClose: () => void;
  onSaved: (msg: string) => void;
}>): JSX.Element {
  const popup = useMovablePopup<HTMLDivElement>();
  const isEdit = state.kind === 'edit';
  const existing = isEdit ? state.account : null;
  const existingFund = existing ? fundLabel(existing) : '';
  const chosen = state.kind === 'create' ? state.entity : null;

  const [form, setForm] = useState({
    // The fund the account is for. Chosen from the funds that are missing one; a fund that
    // is not receiving yet (the next series) from the companies books announced.
    legal_entity_id: chosen?.legal_entity_id ?? '',
    other_fund: false,
    market: existing?.market ?? '',
    currency: existing?.currency ?? chosen?.currency ?? '',
    beneficiary: existing?.beneficiary ?? '',
    transfer_method: existing?.transfer_method ?? '',
    clabe: existing?.clabe ?? '',
    routing_number: existing?.routing_number ?? '',
    account_number: existing?.account_number ?? '',
    transit_number: existing?.transit_number ?? '',
    institution_number: existing?.institution_number ?? '',
    bank_name: existing?.bank_name ?? '',
    is_active: existing?.is_active ?? true,
    effective_from: existing?.effective_from ?? '',
    effective_to: existing?.effective_to ?? '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    globalThis.addEventListener('keydown', onKey);
    return () => globalThis.removeEventListener('keydown', onKey);
  }, [onClose]);

  const set = (key: string, value: string | boolean): void =>
    setForm((f) => ({ ...f, [key]: value }));

  const nullStr = (v: string): string | null => (v.trim() ? v.trim() : null);

  const fundError = isEdit ? null : fundProblem(form.legal_entity_id);
  // An account in force keeps its details for good; only its retirement is edited.
  const frozen = detailsFrozen(existing);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (fundError !== null) {
      setErr(fundError);
      return;
    }
    setErr(null);
    setSubmitting(true);
    try {
      if (isEdit && existing) {
        const retirement = { is_active: form.is_active, effective_to: nullStr(form.effective_to) };
        const patch: ConcentrationAccountPatchBody = frozen
          ? retirement
          : {
              beneficiary: form.beneficiary,
              transfer_method: form.transfer_method,
              clabe: nullStr(form.clabe),
              routing_number: nullStr(form.routing_number),
              account_number: nullStr(form.account_number),
              transit_number: nullStr(form.transit_number),
              institution_number: nullStr(form.institution_number),
              bank_name: nullStr(form.bank_name),
              ...retirement,
            };
        await adminService.updateConcentrationAccount(existing.id, patch);
        onSaved('Account updated');
      } else {
        const body: ConcentrationAccountCreateBody = {
          legal_entity_id: form.legal_entity_id.trim(),
          market: form.market,
          currency: form.currency,
          beneficiary: form.beneficiary,
          transfer_method: form.transfer_method,
          clabe: nullStr(form.clabe),
          routing_number: nullStr(form.routing_number),
          account_number: nullStr(form.account_number),
          transit_number: nullStr(form.transit_number),
          institution_number: nullStr(form.institution_number),
          bank_name: nullStr(form.bank_name),
          effective_from: nullStr(form.effective_from),
        };
        await adminService.createConcentrationAccount(body);
        onSaved('Account entered. It is not in force until a different super admin confirms it.');
      }
    } catch (e) {
      setErr(getApiErrorMessage(e, 'Save failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitLabel = isEdit ? 'Save changes' : 'Enter account';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.45)',
          border: 'none',
          padding: 0,
          cursor: 'default',
        }}
      />
      <div
        ref={popup.panelRef}
        style={{
          ...popup.panelStyle,
          position: 'relative',
          zIndex: 1,
          background: tokens.surface,
          borderRadius: 16,
          padding: 24,
          width: '100%',
          maxWidth: 560,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        {popup.bar}
        <h2 style={{ margin: '0 0 4px', fontSize: 16, color: tokens.textPrimary }}>
          {isEdit ? 'Edit concentration account' : 'Add concentration account'}
        </h2>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: tokens.textMuted }}>
          {isEdit
            ? `Updating ${existing?.market} / ${existing?.currency} — ${existingFund}`
            : 'The bank account of one fund. Clients of the products it receives are shown this account.'}
        </p>

        <form onSubmit={onSubmit} style={{ display: 'grid', gap: 12 }}>
          {!isEdit && (
            <div style={{ display: 'grid', gap: 8 }}>
              <label style={labelStyle}>
                Fund this account belongs to{' '}
                <select
                  value={form.other_fund ? '__other__' : form.legal_entity_id}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '__other__') {
                      setForm((f) => ({ ...f, other_fund: true, legal_entity_id: '' }));
                      return;
                    }
                    const picked = missing.find((m) => m.legal_entity_id === value);
                    setForm((f) => ({
                      ...f,
                      other_fund: false,
                      legal_entity_id: value,
                      currency: picked?.currency ?? f.currency,
                    }));
                  }}
                  style={inputStyle}
                >
                  <option value="">Choose a fund…</option>
                  {missing.map((m) => (
                    <option key={m.legal_entity_id} value={m.legal_entity_id}>
                      {entityOption(m)}
                    </option>
                  ))}
                  <option value="__other__">A fund that is not receiving yet…</option>
                </select>
              </label>
              {form.other_fund && (
                <NotReceivingFund
                  offeredAbove={missing}
                  value={form.legal_entity_id}
                  onPick={(id, currency) =>
                    setForm((f) => ({ ...f, legal_entity_id: id, currency: currency ?? f.currency }))
                  }
                />
              )}
              {fundError !== null && (
                <p style={{ margin: 0, fontSize: 12, color: tokens.textMuted }}>{fundError}</p>
              )}
            </div>
          )}
          {!isEdit && (
            <div style={twoColStyle}>
              <div style={labelStyle}>
                <label htmlFor="concentration-market">Bank country</label>{' '}
                <CountryPicker
                  id="concentration-market"
                  codes={FIRST_COUNTRIES}
                  value={form.market === '' ? null : form.market}
                  onChange={(code) => set('market', code ?? '')}
                  inputStyle={inputStyle}
                />
              </div>
              <label style={labelStyle}>
                Currency (3-char){' '}
                <input
                  required
                  maxLength={3}
                  value={form.currency}
                  onChange={(e) => set('currency', e.target.value.toUpperCase())}
                  style={inputStyle}
                  placeholder="MXN"
                />
              </label>
            </div>
          )}

          {frozen && (
            <p style={{ margin: 0, fontSize: 13, color: tokens.textMuted }}>
              This account is in force, so its details do not change. To change a fund’s
              numbers, add the fund’s new account: it replaces this one when a second super
              admin confirms it. Here you can only retire this account.
            </p>
          )}
          <fieldset
            disabled={frozen}
            style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 12, minWidth: 0 }}
          >
          <label style={labelStyle}>
            Beneficiary{' '}
            <input
              required
              maxLength={255}
              value={form.beneficiary}
              onChange={(e) => set('beneficiary', e.target.value)}
              style={inputStyle}
            />
          </label>

          <label style={labelStyle}>
            Transfer method{' '}
            <input
              required
              maxLength={50}
              value={form.transfer_method}
              onChange={(e) => set('transfer_method', e.target.value)}
              style={inputStyle}
              placeholder="SPEI"
            />
          </label>

          <label style={labelStyle}>
            Bank name{' '}
            <input
              maxLength={255}
              value={form.bank_name}
              onChange={(e) => set('bank_name', e.target.value)}
              style={inputStyle}
            />
          </label>

          <div style={twoColStyle}>
            <label style={labelStyle}>
              CLABE (MX){' '}
              <input
                maxLength={18}
                value={form.clabe}
                onChange={(e) => set('clabe', e.target.value)}
                style={inputStyle}
                placeholder="18 digits"
              />
            </label>
            <label style={labelStyle}>
              Account number{' '}
              <input
                maxLength={50}
                value={form.account_number}
                onChange={(e) => set('account_number', e.target.value)}
                style={inputStyle}
              />
            </label>
          </div>

          <div style={twoColStyle}>
            <label style={labelStyle}>
              Routing number (US){' '}
              <input
                maxLength={9}
                value={form.routing_number}
                onChange={(e) => set('routing_number', e.target.value)}
                style={inputStyle}
                placeholder="9 digits"
              />
            </label>
            <label style={labelStyle}>
              Transit number (CA){' '}
              <input
                maxLength={5}
                value={form.transit_number}
                onChange={(e) => set('transit_number', e.target.value)}
                style={inputStyle}
                placeholder="5 digits"
              />
            </label>
          </div>

          <label style={labelStyle}>
            Institution number (CA){' '}
            <input
              maxLength={3}
              value={form.institution_number}
              onChange={(e) => set('institution_number', e.target.value)}
              style={inputStyle}
              placeholder="3 digits"
            />
          </label>
          </fieldset>

          {!isEdit && (
            <label style={labelStyle}>
              Effective from{' '}
              <input
                type="date"
                value={form.effective_from}
                onChange={(e) => set('effective_from', e.target.value)}
                style={inputStyle}
              />
            </label>
          )}
          {isEdit && (
            <>
              <label style={labelStyle}>
                Effective to (blank = open){' '}
                <input
                  type="date"
                  value={form.effective_to}
                  onChange={(e) => set('effective_to', e.target.value)}
                  style={inputStyle}
                />
              </label>
              <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => set('is_active', e.target.checked)}
                />{' '}
                Active (untick to retire it: its fund’s clients are then shown no account)
              </label>
            </>
          )}

          {err && <div style={errBoxStyle}>{err}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button type="button" style={btnSecondary} onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || (!isEdit && form.market === '')}
              style={btnPrimary}
            >
              {submitting ? 'Saving…' : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  background: tokens.surface,
  border: `1px solid ${tokens.border}`,
  borderRadius: 12,
  padding: 20,
};
const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  color: tokens.textPrimary,
  fontSize: 13,
};
const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px 8px 0',
  fontWeight: 600,
  fontSize: 12,
};
const tdStyle: React.CSSProperties = {
  padding: '12px 12px 12px 0',
  verticalAlign: 'middle',
};
const twoColStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 12,
};
const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 12,
  color: tokens.textLabel,
};
const noteStyle: React.CSSProperties = { margin: 0, fontSize: 12, color: tokens.textMuted };
const inputStyle: React.CSSProperties = {
  background: tokens.surfaceMuted,
  border: `1px solid ${tokens.border}`,
  borderRadius: 8,
  padding: '8px 10px',
  color: tokens.textPrimary,
  fontSize: 13,
};
const btnPrimary: React.CSSProperties = {
  background: tokens.gold,
  color: tokens.navy,
  border: 'none',
  borderRadius: 8,
  padding: '9px 18px',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 13,
};
const btnSecondary: React.CSSProperties = {
  background: tokens.surfaceMuted,
  color: tokens.textPrimary,
  border: `1px solid ${tokens.border}`,
  borderRadius: 8,
  padding: '9px 14px',
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 13,
};
const errBoxStyle: React.CSSProperties = {
  background: tokens.errorBg,
  border: `1px solid ${tokens.errorBorder}`,
  color: tokens.errorText,
  padding: 12,
  borderRadius: 8,
};
const successBoxStyle: React.CSSProperties = {
  background: tokens.successBg,
  border: `1px solid ${tokens.successBorder}`,
  color: tokens.successText,
  padding: 12,
  borderRadius: 8,
  marginBottom: 12,
};
